import {Avatar} from 'antd'
import {member_getMembers_Api} from 'apis'
import {getId, getType} from 'apis/model/base'
import EmptyHolder from 'components/EmptyHolder'
import getAvatar from 'helpers/getAvatar'
import getLinkToDetail from 'helpers/getLinkToDetail'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import CustomLazyPaginationWrapper from 'modules/asyncCache/components/CustomLazyPaginationWrapper'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useState} from 'react'
import {BsPersonFill} from 'react-icons/bs'
import {Link} from 'react-router-dom'
import {ConversationWith} from 'views/AppLocals/factory/createChatButton'
import createStickySider from 'views/AppLocals/factory/createStickySider'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {EventHosts} from 'views/AppLocals/Host/components/HostFactory'
import {renderShowMore} from 'views/MainPage/functions/renderLimitLoadMore'
import renderPagingLoading from 'views/MainPage/functions/renderPagingLoading'
import renderPagingWrapper from 'views/MainPage/functions/renderPagingWrapper'
import {renderSelf, withPagerWrapper} from 'views/Shared'
import EventContext from '../EventContext'
import {EventShareWidget} from '../../factory/createShareWidget'
import {renderIf} from '../../../Shared'
import {BiSolidChurch} from "react-icons/bi";
import {FaUserTie} from "react-icons/fa";
import {RiMessage3Line} from "react-icons/ri";
import {RequiredSystemAdmin} from "../../../../components/RequiredAdmin";
import {HeaderBlock} from "../../../MainPage/components/HeaderBlock";
import {ifThenElseMultipleConditions} from "../../../../helpers/ifThenElseMultipleConditions";
import {LayoutContext} from "../../../../components/layouts/Default/LayoutContext";
import {EventClaimReward} from "./EventClaimReward";

const EventOwner = () => {
    const t = useTranslate()

    const {event} = useContext(EventContext)

    const {owner} = event ?? {}

    return (
        <div className="flex flex-col gap-3">
            <HeaderBlock
                size={26}
                icon={<BiSolidChurch size={14} className="text-primary-400"/>}
                title="organization by"
                className="bg-primary-50 text-sm text-primary-600"
            />
            <div className="ml-2 flex items-center gap-2">
                <Avatar
                    src={getAvatar(owner)}
                    icon={<BiSolidChurch className="text-color-300"/>}
                    className="flex flex-center background-200 border border-color-50 rounded-md"
                />
                <div className="flex flex-col flex-1">
                    <Link
                        className="text-primary font-bold text-sm max-lines-1 no-underline leading-tight"
                        to={getLinkToDetail(owner)}>
                        {getTitle(owner)}
                    </Link>
                    <span className="font-medium text-xs text-blue-600 italic">
                        {t(getType(owner))}
                    </span>
                </div>
            </div>
        </div>
    )
}

const EventMember = ({alwaysVisible = false}) => {
    const t = useTranslate()
    const {event} = useContext(EventContext)

    const [limit, setLimit] = useState(5)

    const [id, prop] = [getId(event), getType(event)]
    const apiInfo = member_getMembers_Api
    const lookupKey = apiInfo.path.replace(':id', id).replace(':prop', prop)

    const renderWrap = (alwaysVisible ? renderSelf : withPagerWrapper)(
        renderPagingWrapper()
    )

    const renderMember = (member) => {
        const {user: item} = member ?? {}

        return (
            <Translate key={getId(item)}>
                {(t) => renderIf(
                    member?.visible !== 1,
                    <div className="flex items-center gap-2">
                        <Avatar
                            size={28}
                            src={getAvatar(item)}
                            icon={<BsPersonFill size={14} className="text-color-400"/>}
                            className="flex flex-center background-200"
                        />
                        <div className="flex flex-col flex-1 truncate">
                            <Link
                                to={getLinkToDetail(item)}
                                className="font-semibold text-sm text-color-000 leading-tight no-underline truncate">
                                {getTitle(item)}
                            </Link>
                            <div className="flex items-center justify-between gap-2">
                                {ifThenElseMultipleConditions(
                                    <span
                                        className="font-medium text-2xs text-color-400 italic tracking-wider leading-tight">
                                        {t('member')}
                                    </span>,
                                    member?.role_id === 1,
                                    <span
                                        className="font-medium text-2xs text-primary-600 italic tracking-wider leading-tight">
                                        {t('owner')}
                                    </span>,
                                    member?.role_id === 2,
                                    <span
                                        className="font-medium text-2xs text-blue-500 italic tracking-wider leading-tight">
                                        {t('admin')}
                                    </span>,
                                )}
                                <RequiredSystemAdmin>
                                    <ConversationWith
                                        params={{
                                            receiver_id: getId(item),
                                        }}
                                        render={(__, {onClick}) => (
                                            <div
                                                onClick={onClick}
                                                className="flex items-center gap-1 text-2xs text-color-300 hover:text-primary italic tracking-wide leading-tight cursor-pointer">
                                                {t('send message')}
                                                <RiMessage3Line size={10}/>
                                            </div>
                                        )}
                                    />
                                </RequiredSystemAdmin>
                            </div>
                        </div>
                    </div>
                )}
            </Translate>
        )
    }


    return (
        <div className="flex flex-col gap-3">
            <HeaderBlock
                size={26}
                icon={<BsPersonFill size={14} className="text-color-400"/>}
                title="co-hosts"
                className="background-200 text-sm text-color-200"
                renderTitle={() => (
                    <div className="flex items-center gap-2">
                        <span className="flex-1 uppercase text-sm font-bold text-color-200">
                            {t('list of members')}
                        </span>
                        <span className="text-2xs text-primary-500 italic lowercase tracking-wide">
                            {event?.total?.members} {t('member')}
                        </span>
                    </div>
                )}
            />
            <div className="ml-2 flex flex-col gap-3">
                <CustomLazyPaginationWrapper
                    auto={false}
                    sample={limit}
                    id={lookupKey}
                    apiInfo={apiInfo}
                    values={{visible: 1}}
                    renderWrap={renderWrap}
                    renderItem={renderMember}
                    alwaysVisible={alwaysVisible}
                    renderLoading={renderPagingLoading}
                    renderEmpty={() => <EmptyHolder/>}
                    query={bindQueryParams([{id}, {prop}])}
                    renderLoadMoreButton={renderShowMore([limit, setLimit])}
                />
                <div className="p-2 shadow-xs background rounded-md border-0 text-xs text-color-300 italic">
                    {t('Some participants are not displayed due to privacy policy.')}
                </div>
            </div>
        </div>
    )
}

const EventSpeaker = () => {
    const t = useTranslate()

    const {event} = useContext(EventContext)

    const {speakers = []} = event ?? {}

    return renderIf(
        speakers.length > 0,
        <div className="flex flex-col gap-3">
            <HeaderBlock
                size={26}
                icon={<FaUserTie size={14} className="text-blue-400"/>}
                title="speakers"
                className="bg-blue-100 text-sm text-blue-600"
            />
            <div className="ml-2 flex flex-col gap-3">
                {speakers.map((e, i) => (
                    <div key={i} className="flex items-center gap-2">
                        <Avatar
                            size={28}
                            src={_.get(e, 'speaker.avatar')}
                            icon={<FaUserTie size={12} className="text-color-400"/>}
                            className="flex flex-center background-200"
                        />
                        <Link
                            className="font-bold text-color-000 text-sm max-lines-1 no-underline leading-tight"
                            to={getLinkToDetail(e.speaker)}>
                            {_.get(e, 'speaker_name')}
                        </Link>
                    </div>
                ))}
            </div>
        </div>
    )
}

const RightSider = () => {
    const t = useTranslate()
    const {isSm, isLg} = useContext(LayoutContext)

    return isLg ? (
        <div className="flex flex-col gap-6">

            <EventClaimReward/>

            <EventOwner/>
            <EventHosts/>
            <EventSpeaker/>
            <EventMember/>
            <EventShareWidget
                className="gap-2"
                header={
                    <Translate>
                        {(t) => (
                            <span className="text-sm text-color-400 italic whitespace-nowrap">
                            {`${t('share')} : `}
                        </span>
                        )}
                    </Translate>
                }
            />
        </div>
    ) : (
        <div className="flex flex-col gap-6">
            <EventClaimReward/>
        </div>
    )
}

const EventSider = ({Wrapper = 'div', stickySider = true}) =>
    createStickySider({
        Wrapper,
        stickySider,
    })(RightSider)

export default EventSider
